/**
 * G-18 — kiosk presence: capture the tablet's position once per minute and
 * ride it on every floor mutation. The server compares against the shop pin
 * (150 m) and stamps OK / FAR / UNKNOWN on the audit row; a denied or absent
 * position is never a reason to block the action — UNKNOWN is itself a flag.
 *
 * Coordinates are rounded to 6 decimal places (~11 cm) because the backend
 * KioskGeoDto validates maxDecimalPlaces: 6.
 */

export interface KioskGeoFields {
  geoLat?: number;
  geoLng?: number;
  geoAccuracyM?: number;
}

const GEO_CACHE_MS = 60_000;
const GEO_TIMEOUT_MS = 5_000;

let cache: { fields: KioskGeoFields; at: number } | null = null;
let inFlight: Promise<KioskGeoFields> | null = null;

const round6 = (n: number) => Math.round(n * 1_000_000) / 1_000_000;

/**
 * Resolve the current position, cached for a minute; {} when unavailable.
 *
 * Failures are cached too (review 2026-08-19): a tablet whose geolocation
 * never yields a fix must not pay the full timeout before EVERY sale —
 * "never block" includes never slowing the register. Concurrent callers
 * share one in-flight lookup for the same reason.
 */
export function currentKioskGeo(): Promise<KioskGeoFields> {
  if (cache && Date.now() - cache.at < GEO_CACHE_MS) {
    return Promise.resolve(cache.fields);
  }
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    return Promise.resolve({});
  }
  if (inFlight) return inFlight;
  inFlight = new Promise((resolve) => {
    let settled = false;
    const settle = (fields: KioskGeoFields) => {
      if (settled) return;
      settled = true;
      cache = { fields, at: Date.now() };
      inFlight = null;
      resolve(fields);
    };
    // Belt over the API's own timeout — some engines ignore it when the
    // permission prompt is up; the action must not hang behind the prompt.
    const timer = window.setTimeout(() => settle({}), GEO_TIMEOUT_MS + 500);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        window.clearTimeout(timer);
        const fields = {
          geoLat: round6(pos.coords.latitude),
          geoLng: round6(pos.coords.longitude),
          geoAccuracyM: Math.round(pos.coords.accuracy),
        };
        // A fix that arrives AFTER the belt settled (the user answered the
        // permission prompt late) still refreshes the cache, so the next
        // action carries real coordinates instead of a cached blank.
        cache = { fields, at: Date.now() };
        settle(fields);
      },
      () => {
        window.clearTimeout(timer);
        settle({});
      },
      {
        enableHighAccuracy: false,
        timeout: GEO_TIMEOUT_MS,
        maximumAge: GEO_CACHE_MS,
      },
    );
  });
  return inFlight;
}

/**
 * Fire-and-forget permission priming: call on kiosk page load so the
 * browser's location prompt happens BEFORE the first arming, not during it
 * — an unanswered prompt at arm time stamps the whole session UNKNOWN
 * (review 2026-08-19). Also warms the cache.
 */
export function primeKioskGeo(): void {
  void currentKioskGeo();
}

/** Merge the geo stamp into a JSON mutation body. */
export async function withKioskGeo<T extends object>(
  body: T,
): Promise<T & KioskGeoFields> {
  return { ...body, ...(await currentKioskGeo()) };
}

/** Append the geo stamp to a multipart body as text fields. */
export async function appendKioskGeo(form: FormData): Promise<FormData> {
  const geo = await currentKioskGeo();
  if (geo.geoLat != null) form.append("geoLat", String(geo.geoLat));
  if (geo.geoLng != null) form.append("geoLng", String(geo.geoLng));
  if (geo.geoAccuracyM != null) {
    form.append("geoAccuracyM", String(geo.geoAccuracyM));
  }
  return form;
}
